import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Interface pour les options de nettoyage HTML
 */
export interface CleanHtmlOptions {
  /** Remplacer les &nbsp; par des espaces normaux */
  cleanNbsp?: boolean;
  /** Nettoyer les espaces multiples */
  cleanMultipleSpaces?: boolean;
  /** Supprimer les classes Quill */
  removeQuillClasses?: boolean;
  /** Supprimer les attributs data Quill */
  removeQuillDataAttributes?: boolean;
  /** Supprimer les styles inline */
  removeInlineStyles?: boolean;
  /** Supprimer les balises vides */
  removeEmptyTags?: boolean;
  /** Nettoyer automatiquement à l'initialisation */
  cleanOnInit?: boolean;
}

/**
 * Options par défaut pour le nettoyage HTML
 */
const defaultCleanOptions: CleanHtmlOptions = {
  cleanNbsp: true,
  cleanMultipleSpaces: true,
  removeQuillClasses: true,
  removeQuillDataAttributes: true,
  removeInlineStyles: false, // Conservé pour ne pas casser les styles utilisateur
  removeEmptyTags: true,
  cleanOnInit: true,
};

/**
 * Fonction utilitaire pour nettoyer le HTML
 * @param html - Le HTML à nettoyer
 * @param options - Options de nettoyage
 * @returns Le HTML nettoyé
 */
export function cleanHtmlContent(html: string, options: CleanHtmlOptions = {}): string {
  if (!html || typeof html !== 'string') {
    return html;
  }

  const opts = { ...defaultCleanOptions, ...options };
  let cleanedHtml = html;

  // 1. Remplacer les &nbsp; par des espaces normaux
  if (opts.cleanNbsp) {
    cleanedHtml = cleanedHtml.replace(/&nbsp;/g, ' ');
  }

  // 2. Nettoyer les espaces multiples (mais préserver les espaces dans les balises)
  if (opts.cleanMultipleSpaces) {
    cleanedHtml = cleanedHtml.replace(/>\s+</g, '><'); // Entre balises
    cleanedHtml = cleanedHtml.replace(/\s{2,}/g, ' '); // Espaces multiples dans le texte
  }

  // 3. Nettoyer les attributs Quill spécifiques
  if (opts.removeQuillClasses) {
    cleanedHtml = cleanedHtml.replace(/\sclass="[^"]*ql-[^"]*[^"]*"/g, '');
    cleanedHtml = cleanedHtml.replace(/\sclass=""/g, ''); // Supprimer les class vides
  }

  if (opts.removeQuillDataAttributes) {
    cleanedHtml = cleanedHtml.replace(/\sdata-ql-[^=]*="[^"]*"/g, '');
  }

  // 4. Nettoyer les styles inline (optionnel)
  if (opts.removeInlineStyles) {
    cleanedHtml = cleanedHtml.replace(/\sstyle="[^"]*"/g, '');
  }

  // 5. Nettoyer les balises vides
  if (opts.removeEmptyTags) {
    // Plusieurs passes pour les balises imbriquées vides
    let previousHtml;
    do {
      previousHtml = cleanedHtml;
      cleanedHtml = cleanedHtml.replace(/<([^>]+)>\s*<\/\1>/g, '');
      cleanedHtml = cleanedHtml.replace(/<([^>]+)><\/\1>/g, '');
    } while (cleanedHtml !== previousHtml);
  }

  // 6. Nettoyer les espaces en début et fin
  cleanedHtml = cleanedHtml.trim();

  return cleanedHtml;
}

/**
 * Extension TipTap pour nettoyer automatiquement le contenu HTML
 */
export const pasteCleanExtension = Extension.create<CleanHtmlOptions>({
  name: 'pasteClean',

  addOptions() {
    return defaultCleanOptions;
  },

  onCreate(): void {
    // Nettoyer le contenu à l'initialisation si l'option est activée
    if (this.options.cleanOnInit) {
      const currentContent = this.editor.getHTML();
      const cleanedContent = cleanHtmlContent(currentContent, this.options);

      if (cleanedContent !== currentContent) {
        // Utiliser setTimeout pour éviter les conflits d'initialisation
        setTimeout(() => {
          this.editor.commands.setContent(cleanedContent, false);
        }, 0);
      }
    }
  },

  onUpdate(): void {
    // Optionnel : nettoyer le contenu à chaque mise à jour
    // Attention : peut être coûteux en performance
    // if (this.options.cleanOnUpdate) {
    //   this.editor.commands.cleanContent();
    // }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('pasteClean'),
        props: {
          handlePaste: (view, event): boolean => {
            const clipboardData = event.clipboardData;
            if (!clipboardData) {
              return false;
            }

            const htmlData = clipboardData.getData('text/html');

            // Si pas de HTML, laisser le comportement par défaut
            if (!htmlData) {
              return false;
            }

            // Nettoyer le HTML avec les options configurées
            const cleanedHtml = cleanHtmlContent(htmlData, this.options);

            // Si le HTML a été modifié, l'insérer
            if (cleanedHtml !== htmlData) {
              event.preventDefault();

              // Utiliser la commande insertContent de TipTap
              this.editor.commands.insertContent(cleanedHtml);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
